import { SchedulerFilters } from '@/types/scheduler.types';

export const defaultFilters: SchedulerFilters = {
  search: '',
  date: null,
  time: null,
  status: '',
  user: '',
  edit_date: null
};

export const getNextExpandedEntries = (currentExpanded: number[], entryId: number): number[] => {
  return currentExpanded.includes(entryId)
    ? currentExpanded.filter(id => id !== entryId)
    : [...currentExpanded, entryId];
};

export const formatDateTime = (dateTime: string) =>
  new Date(dateTime).toLocaleString('uk-UA', { timeZone: 'Europe/Kiev' });
export const formatDate = (dateTime: string) =>
  new Date(dateTime).toLocaleDateString('uk-UA', { timeZone: 'Europe/Kiev' });
export const formatTime = (dateTime: string) =>
  new Date(dateTime).toLocaleTimeString('uk-UA', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    timeZone: 'Europe/Kiev'
  });

/**
 * Mapping of status codes to Ukrainian display labels
 */
export const statusLabels: Record<string, string> = {
  processing: 'Опрацьовується',
  completed: 'Виконано',
  cancelled: 'Скасовано',
  failed: 'Помилка'
};

/**
 * Array of all available status options
 */
export const statusOptions = ['processing', 'completed', 'cancelled', 'failed'];

/**
 * Mapping of substage types to Ukrainian display labels
 */
export const substageTypeLabels = {
  notification: 'Сповіщення',
  price_1c: 'Зміна ціни БО',
  price_okko_core: 'Зміна ціни на касі'
};

/**
 * Array of status values that can be edited by users
 */
export const editableStatuses = ['processing', 'cancelled'];

export const isDue = (datetime: string | null | undefined): boolean => {
  if (!datetime) return false;
  return new Date(datetime) <= new Date();
};

export const removeKeyFromMap = <T extends Record<string, any>>(map: T, key: string): T => {
  const copy = { ...map } as T;
  delete copy[key];
  return copy;
};

export const setMapEntryField = (
  map: Record<string, any>,
  key: string,
  field: string,
  value: any
): Record<string, any> => {
  const copy: Record<string, any> = { ...map };
  if (!copy[key]) copy[key] = {};
  copy[key] = { ...copy[key], [field]: value };
  return copy;
};

export const applyEditedValueToEntries = (
  entries: any[],
  type: 'entry' | 'substage',
  id: number,
  editedValue: Record<string, any>
) => {
  if (!Array.isArray(entries)) return entries;
  if (type === 'entry') {
    return entries.map(e => (e.id === id ? { ...e, ...editedValue } : e));
  }

  return entries.map(entry => ({
    ...entry,
    substages: entry.substages?.map((s: any) => (s.id === id ? { ...s, ...editedValue } : s))
  }));
};

export const editKey = (type: 'entry' | 'substage', id: number) => `${type}-${id}`;

export const addOrReplacePendingUpdate = (
  prev: { type: 'entry' | 'substage'; id: number }[],
  update: { type: 'entry' | 'substage'; id: number }
) => {
  const filtered = prev.filter(u => !(u.type === update.type && u.id === update.id));
  return [...filtered, update];
};

export const previewApplyPendingUpdates = (
  entries: any[],
  pending: { type: 'entry' | 'substage'; id: number; [k: string]: any }[]
) => pending.reduce((acc, upd) => applyEditedValueToEntries(acc, upd.type, upd.id, upd), entries || []);
